import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useToggle } from 'react-use';
import { useGetActionsQuery } from 'apis/cm';
import { useGetCountersQuery } from 'apis/pm';
import { selectUserPreference } from 'redux/UserPreferences';
import { selectUserPreferences } from 'redux/selectors';
import { ProgressIndicator } from 'components/shared/Progress';
import { getTimeRange } from 'components/shared/TimeSelect';
import { findBestResolution, adjustTimeRange } from 'components/shared/Chart';
import { DeviceChart, DeviceChartError, DeviceChartTitle } from 'components/shared/DeviceChart';
import styles from './LTESignalChart.module.css';

function LTESignalChart({ deviceId, preferenceId, title }) {
  const [actionVisible, toggleActionVisible] = useToggle(true);
  const preference = useSelector((state) => selectUserPreference(selectUserPreferences(state), preferenceId));
  const {
    name,
    state: {
      query: { counters, actions, time },
      datasets,
      scales
    }
  } = preference;
  const query = useMemo(
    () => ({
      devices: [deviceId],
      counters,
      actions,
      time
    }),
    [deviceId, counters, actions, time]
  );
  const timeRange = useMemo(() => getTimeRange(time), [time]);
  const resolution = useMemo(() => {
    const [from, to] = timeRange;
    const duration = new Date(to).getTime() - new Date(from).getTime();

    return findBestResolution(480, duration);
  }, [timeRange]);
  const [from, to] = useMemo(
    () => (resolution ? adjustTimeRange(timeRange, resolution) : timeRange),
    [timeRange, resolution]
  );
  const countersQuery = useGetCountersQuery(
    {
      devices: query.devices,
      counters: query.counters,
      from,
      to,
      interval: resolution
    },
    {
      placeholderData: [],
      retry: (failureCount, error) => error?.response?.status !== 500 && failureCount < 3
    }
  );
  const actionsQuery = useGetActionsQuery(
    {
      devices: query.devices,
      actions: query.actions,
      runtimeStatuses: ['Completed'],
      from,
      to
    },
    {
      enabled: actions?.length > 0,
      placeholderData: []
    }
  );

  return (
    <div className={styles.root}>
      <div className={styles.title}>{title}</div>
      <div className={styles.container}>
        {countersQuery.error != null ? (
          <DeviceChartError retrying={countersQuery.isFetching} />
        ) : (
          <div className={styles.chart}>
            <div className={styles.chartHeader}>
              <DeviceChartTitle title={name} />
            </div>
            <div className={styles.chartBody}>
              {countersQuery.isFetching && <ProgressIndicator className={styles.progress} />}
              <DeviceChart
                counters={countersQuery.data}
                actions={actionsQuery.data}
                datasets={datasets}
                scales={scales}
                actionsEnabled={actions?.length > 0}
                actionsVisible={actionVisible}
                onClickActionVisible={toggleActionVisible}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LTESignalChart;
